import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { differenceInSeconds } from 'date-fns';

import { env } from 'config/env';

import { useIsOnline } from 'hooks/online';
import { getSavedUserInfo } from 'providers/Auth';

import { getID } from 'utils/id';

import { RealTimeContext } from './realTime.context';
import { MessagePayload, MessageType, ServerMessage } from './types';
import { SyncAdvise } from './SyncAdvise';

const SERVICE_URL = env.webSocketUrl;

export function RealTimeProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState(() => getSavedUserInfo().data?.id || null);

  const isOnline = useIsOnline();

  const [loadingCount, setLoadingCount] = useState(0);

  const socket = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<number>();
  const pingControl = useRef<Date>();
  const [isConnected, setIsConnected] = useState(false);

  const onSocketOpen = (): void => {
    console.log('ws opened');

    setIsConnected(true);
  };

  const onSocketClosed = (): void => {
    console.log('ws closed');
    socket.current = null;
    setIsConnected(false);
  };

  /*
   **
   ** This repetition is intentional,
   ** to make sure we have the correct closure
   ** after every state update
   **
   */

  const updateRef = useRef<(newValue: ServerMessage) => Promise<void>>(
    async (newV: ServerMessage) => {
      console.log(newV);
    },
  );

  const customHandlers = useRef<{
    [key in MessageType]?: Array<{ handler: (message: MessagePayload) => void; id: string }>;
  }>({});

  const registerHandler = useCallback(
    (type: MessageType, handler: (message: MessagePayload) => void) => {
      const id = getID();

      customHandlers.current = {
        ...customHandlers.current,

        [type]: [...(customHandlers.current[type] || []), { handler, id }],
      };

      // we can use memory pointers, maybe
      return id;
    },
    [],
  );

  const unregisterHandler = useCallback((type: MessageType, id: string): void => {
    customHandlers.current = {
      ...customHandlers.current,

      [type]: (customHandlers.current[type] || []).filter((handler) => handler.id !== id),
    };
  }, []);

  updateRef.current = async ({ updates }: ServerMessage): Promise<void> => {
    if (!updates) return;

    const byType =
      updates.reduce?.(
        (acc, next) => ({
          ...acc,
          [next.type]: [...(acc[next.type] || []), next.payload],
        }),
        {} as Record<MessageType, MessagePayload[]>,
      ) || {};

    Object.entries(byType).forEach(([type, messages]) => {
      customHandlers.current[type as MessageType]?.forEach(({ handler }) => {
        messages.forEach((message) => handler(message));
      });
    });
  };

  const onMessage = async ({ data }: MessageEvent): Promise<void> => {
    if (!data) return;

    updateRef.current?.(JSON.parse(data) as ServerMessage);
  };

  const connectSocket = (): void => {
    if (!userId || isConnected) return;

    try {
      if (!socket.current) {
        socket.current = new WebSocket(`${SERVICE_URL}?u=${userId}`);
      }

      if (socket.current?.readyState !== WebSocket.OPEN) {
        socket.current.addEventListener('open', onSocketOpen);
        socket.current.addEventListener('message', onMessage);
        socket.current.addEventListener('close', onSocketClosed);
      }
    } catch {
      console.log('Websocket failed. Retrying...');
    }
  };

  const sendPing = useCallback(() => {
    const lastPing = pingControl.current;

    const PING_INTERVAL_MINUTES = 4;

    const now = new Date();

    // minutes causes to round down and actually be 5 instead of 4
    const shouldSend =
      !lastPing || Math.abs(differenceInSeconds(lastPing, now)) > PING_INTERVAL_MINUTES * 60;

    if (!shouldSend) return;

    socket.current?.send('ping');

    pingControl.current = now;
  }, []);

  useEffect(() => {
    if (!userId) return socket.current?.close();

    connectSocket();

    if (!isOnline) socket.current?.close();

    reconnectTimer.current = window.setInterval(() => {
      if (socket.current?.readyState === WebSocket.OPEN) return sendPing();

      console.log('trying to connect...');

      connectSocket();
    }, 5_000);

    const closeOnLeave = (): void => {
      socket.current?.close();
    };

    window.addEventListener('beforeunload', closeOnLeave);

    return () => {
      window.clearInterval(reconnectTimer.current);
      window.removeEventListener('beforeunload', closeOnLeave);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isConnected, isOnline]);

  const contextData = useMemo(
    () => ({
      registerHandler,
      unregisterHandler,
      setUserId,

      loading: {
        up: () => setLoadingCount((old) => old + 1),
        down: () => setLoadingCount((old) => Math.max(0, old - 1)),
      },
    }),
    [unregisterHandler, registerHandler, setUserId],
  );

  return (
    <RealTimeContext.Provider value={contextData}>
      {children}

      {!!loadingCount && <SyncAdvise />}
    </RealTimeContext.Provider>
  );
}
